import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import React, { PropsWithChildren } from "react";
import { memo } from "react";
import { LSResultItemCheckbox } from "../LSResultItem/LSResultItemCheckbox";
import { LSResultItemBookjacket } from "../LSResultItem/LSResultItemBookjacket";
import { RateAndReview } from "@tlc/ls2pac/components/rate-and-review/RateAndReview";
import { LSResultItemAvailability } from "../LSResultItem/LSResultItemAvailability";
import { RequestButton } from "@tlc/ls2pac/components/place-hold/RequestButton";
import { LSResultItemAddToListButton } from "../LSResultItem/LSResultItemAddToListButton";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { ViewSiteButton } from "../../../news/ViewSiteButton";
import { DownloadButton } from "../../../resource/DownloadButton";
import { DownloadableItemAvailability } from "../DownloadableItemAvailability";
import { ResourceSeriesLink } from "../ResourceSeriesLink";
import { ContentDMOrRSSFormat } from "../ContentDMOrRSSItem";

interface GridItemLayoutProps {
  checkboxComponent: React.ReactNode;
  bookjacketComponent: React.ReactNode;
  availabilityComponent: React.ReactNode;
  actionsComponent: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const GridItemLayout = ({
  bookjacketComponent,
  checkboxComponent,
  availabilityComponent,
  actionsComponent,
  children,
  sx = {},
}: PropsWithChildren<GridItemLayoutProps>) => {
  const isMobile = useIsMobile();
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        ...sx,
      }}
    >
      <Box sx={{ alignSelf: isMobile ? "flex-end" : "flex-start" }}>
        {checkboxComponent}
      </Box>
      <CardMedia>{bookjacketComponent}</CardMedia>
      <CardContent sx={{ flexGrow: 1, p: 1, textAlign: "center" }}>
        {children}
      </CardContent>
      {availabilityComponent}
      <CardActions>{actionsComponent}</CardActions>
    </Card>
  );
};

interface GridResultItemProps {
  resource: ResourceView;
  sx: SxProps;
}

export const GridResultItem = memo(
  function SearchResult({ resource, sx }: GridResultItemProps) {
    if (resource.rtype !== 1) {
      return <ContentDMOrRSSGridItem resource={resource} sx={sx} />;
    }

    // DoanloadableItemAvailability is already condensed
    const availabilityComponent = resource.downloadable ? (
      <DownloadableItemAvailability resource={resource} dense={true} />
    ) : (
      <LSResultItemAvailability resource={resource} dense={true} />
    );

    const primaryAction = resource.downloadable ? (
      <DownloadButton resource={resource} />
    ) : (
      <RequestButton resource={resource} />
    );

    return (
      <GridItemLayout
        sx={sx}
        checkboxComponent={<LSResultItemCheckbox resource={resource} />}
        bookjacketComponent={<LSResultItemBookjacket resource={resource} />}
        availabilityComponent={availabilityComponent}
        actionsComponent={
          <>
            {primaryAction}
            <LSResultItemAddToListButton
              dense={true}
              resource={resource}
            ></LSResultItemAddToListButton>
          </>
        }
      >
        <>
          <RateAndReview material={resource}></RateAndReview>

          {resource.format && (
            <Box>
              <Chip variant="outlined" label={resource.format} />
            </Box>
          )}
          <Typography>
            <ResourceSeriesLink resource={resource} />
          </Typography>
        </>
      </GridItemLayout>
    );
  },
  (prevProps, nextProps) => prevProps.resource.id === nextProps.resource.id,
);

export function GridResultItemSkeleton({ sx }: { sx: SxProps }) {
  return (
    <GridItemLayout
      sx={sx}
      checkboxComponent={
        <Skeleton
          sx={{ margin: "9px" }}
          variant={"rectangular"}
          width={24}
          height={24}
        />
      }
      bookjacketComponent={
        <Skeleton variant="rectangular" width={132} height={200} />
      }
      availabilityComponent={null}
      actionsComponent={
        <Skeleton variant="rounded" width={130} height={36.5} />
      }
    >
      <Stack direction="row">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="circular" width={24} height={24} />
        ))}
      </Stack>
    </GridItemLayout>
  );
}

function ContentDMOrRSSGridItem({ resource, sx }: GridResultItemProps) {
  const actionsComponent = (
    <>
      <ViewSiteButton site={resource} />
      <LSResultItemAddToListButton resource={resource} dense={true} />
    </>
  );
  return (
    <GridItemLayout
      sx={sx}
      checkboxComponent={<LSResultItemCheckbox resource={resource} />}
      bookjacketComponent={<LSResultItemBookjacket resource={resource} />}
      availabilityComponent={null}
      actionsComponent={actionsComponent}
    >
      <Box>
        <ContentDMOrRSSFormat resource={resource} />
      </Box>
    </GridItemLayout>
  );
}
